<template>
  <v-container fluid>
    <div class="toolbar d-flex flex-row elevation-3 mb-3">
      <v-row>
        <v-col
          cols="12"
          md="3">
          <span style="font-weight: bold; font-size: 1.25rem;">
            Product Information
          </span>
        </v-col>
        <v-col
          cols="12"
          md="9"
          style="gap: 0.625rem;"
          class="d-flex justify-end align-end"
        >
          <v-btn
            color="primary"
            outlined
            small
            :loading="loading"
            @click="isModalOpen = true">
            Import CSV XLSX
          </v-btn>
          <v-btn
            color="primary"
            outlined
            small
            :disabled="items.length === 0"
            :loading="loading"
            @click="exportForUpdate()">
            Export For Update ({{ totalProduct }})
          </v-btn>
          <v-btn
            color="primary"
            outlined
            small
            :disabled="items.length === 0"
            :loading="loading"
            @click="exportProduct()">
            Export ({{ totalProduct }})
          </v-btn>
        </v-col>

        <div
          class="search-tag"
          style="width: 100%; margin-bottom: 10px; display: flex; justify-content: end; align-items: end;">
          <div style="display: flex; margin-right: 10px; margin-top: 4px; ">
            <v-autocomplete
              v-model="formData.country"
              placeholder="Country"
              color="secondary"
              prepend-inner-icon="mdi-flag-variant-outline"
              :items="shippingCountries"
              item-text="country"
              item-value="code"
              dense
              solo
              hide-details
              :loading="loading" />
          </div>

          <div style="width: 100%;">
            <search-by-tags-and-brand
              v-model="formData"
              append-icon="mdi-magnify"
              tags-width="300px"
              :is-mobile="isMobile"
              @on-search="searchProduct()" />
          </div>
        </div>
      </v-row>
    </div>

    <v-row no-gutters>
      <v-col
        cols="12"
        md="3"
      >
        <div class="collections-container elevation-3">
          <v-text-field
            v-model="collectionSearch"
            placeholder="Search Collections"
            hide-details
            clearable
            solo
            dense
            color="secondary"
            :loading="loadingCollection"
          >
            <template #append>
              <v-icon>
                mdi-magnify
              </v-icon>
            </template>
          </v-text-field>

          <div class="table-collection pt-4">
            <v-list
              ref="collectionList"
              class="list-collection"
              @scroll="onScroll"
            >
              <div v-if="filteredCollections.selected.length > 0">
                <v-list-item
                  v-for="collection in filteredCollections.selected"
                  :key="'selected-' + collection.id"
                >
                  <v-list-item-content class="pa-0">
                    <v-checkbox
                      v-model="selectedCollections"
                      class="ma-0 pa-0"
                      color="black"
                      :value="collection.id"
                      hide-details
                    >
                      <template v-slot:label>
                        <span style="color: black; font-weight: 500;">{{ collection.name }}</span>
                      </template>
                    </v-checkbox>
                  </v-list-item-content>
                </v-list-item>

                <v-divider
                  class="my-2 mx-2"
                  style="border-width: 1px; border-color: rgba(0,0,0,0.12);" />
              </div>

              <v-list-item
                v-for="collection in filteredCollections.unselected"
                :key="collection.id"
              >
                <v-list-item-content class="pa-0">
                  <v-checkbox
                    v-model="selectedCollections"
                    class="ma-0 pa-0"
                    color="black"
                    :value="collection.id"
                    hide-details
                  >
                    <template v-slot:label>
                      <span style="color: black">{{ collection.name }}</span>
                    </template>
                  </v-checkbox>
                </v-list-item-content>
              </v-list-item>

              <div
                v-if="loading || loadingCollection"
                class="pa-4 text-center">
                <v-progress-circular
                  indeterminate
                  size="24" />
              </div>
              <div
                v-if="!loadingCollection && filteredCollections.all.length === 0 && !loading"
                class="pa-4 text-center">
                No results
              </div>
            </v-list>
          </div>
        </div>
      </v-col>

      <v-col
        cols="12"
        md="9"
        class="pl-3">
        <v-form
          ref="form"
          @submit.prevent="updateAllItems()">
          <v-row>
            <v-col cols="12">
              <div class="table-wrapper elevation-3">
                <div class="table-body">
                  <v-data-table
                    :headers="headers"
                    :items="items"
                    :items-per-page="items.length"
                    :loading="loading"
                    height="calc(100vh - 285px)"
                    no-data-text="There aren't any products. :("
                    hide-default-footer>
                    <template #[`item.images`]="{ index }">
                      <div class="d-flex flex-row justify-center align-center">
                        <v-img
                          :src="imageUrl(items[index].images[0])"
                          :lazy-src="lazyImageUrl(items[index].images[0])"
                          width="125"
                          height="125"
                          contain />
                      </div>
                    </template>
                    <template #[`item.color.name`]="{ index }">
                      {{ items[index].color.name.toUpperCase() }}
                    </template>
                    <template #[`item.size.name`]="{ index }">
                      {{ items[index].size.name.toUpperCase() }}
                    </template>
                    <template #[`item.merCode`]="{ index }">
                      {{ getMerCode(items[index].productName).toUpperCase() }}
                    </template>
                    <template #[`item.thbPrice`]="{ index }">
                      {{ showPriceFormat(items[index].thbPrice) }}
                    </template>
                    <template #[`item.price`]="{ index }">
                      {{ showPriceFormat(items[index].price) }}
                    </template>
                    <template #[`item.qrCode`]="{ index }">
                      <div class="d-flex flex-row justify-center align-center">
                        <v-img
                          v-if="items[index].qrCode"
                          :src="items[index].qrCode"
                          width="100"
                          height="100"
                          contain />
                      </div>
                    </template>
                    <template #[`item.fabricComposition`]="{ index }">
                      <div
                        v-for="(fabric, fabricIndex) in items[index].fabrics"
                        :key="`fb-${fabricIndex}`"
                        class="pl-2"
                      >
                        <p
                          v-if="fabric.garmentPart"
                          class="mb-0 font-weight-bold"
                        >
                          {{ fabric.garmentPart.en.toUpperCase() }}
                        </p>

                        <div
                          v-for="composition in fabric.compositions"
                          :key="composition.id"
                        >
                          <p class="mb-0">
                            {{ composition.name }} {{ composition.percentage }}%
                          </p>
                        </div>
                      </div>
                    </template>
                  </v-data-table>
                </div>
              </div>
              <v-col cols="12">
                <v-pagination
                  v-model="options.page"
                  :length="pageCount"
                  :disabled="loading"
                  total-visible="7"
                  color="secondary" />
              </v-col>
            </v-col>
          </v-row>
        </v-form>
      </v-col>
    </v-row>

    <ProductInformationImport
      v-if="isModalOpen"
      @close="isModalOpen = false"
      @imported="getItems"
    />
  </v-container>
</template>

<script>
import QRCode from 'qrcode'
import ProductProvider from '@/resources/ProductProvider'
import NoPicture from '@/assets/image/no_picture_available.png'
import SizeValues from '@/assets/js/SizeValues'
import { mapActions } from 'vuex'
import ProductInformationImport from '../components/ProductInformationImport.vue'
import ShippingCountries from '@/assets/json/ShippingCountries.json'
import ExportCurrencyRatesProvider from '@/resources/ExportCurrencyRatesProvider'
import SearchByTagsAndBrand from '@/components/SearchByTagsAndBrand.vue'
import ProductCollectionProvider from '@/resources/ProductCollectionProvider'

const ExportCurrencyRatesService = new ExportCurrencyRatesProvider()
const ProductCollectionService = new ProductCollectionProvider()
const productService = new ProductProvider()

export default {
  components: {
    ProductInformationImport,
    SearchByTagsAndBrand
  },
  data () {
    return {
      isModalOpen: false,
      file: null,
      storeWebUrl: {
        GW: 'https://www.gentlewomanonline.com',
        LW: 'https://www.gentlewomanonline.com',
        GU: 'https://www.gentlewomanonline.com',
        MM: 'https://matter-makers.com'
      },
      shippingCountries: [],
      items: [],
      allBrand: [],
      collections: [],
      totalProduct: 0,
      options: {
        page: 1,
        limit: 25,
        sortBy: ['createdAt'],
        sortDesc: [true]
      },
      optionsCollection: {
        page: 1,
        limit: 50,
        sortBy: ['createdAt'],
        sortDesc: [true]
      },
      totalItemsCollection: 0,
      totalPageCollections: 0,
      loadingCollection: false,
      selectedCollectionObjects: [],
      pageCount: 0,
      loading: false,
      noPicture: NoPicture,
      formData: {
        search: '',
        country: '',
        brand: 'all',
        collections: [],
        tags: [],
        tagOperation: 'OR'
      },
      collectionSearchInput: '',
      collectionSearch: '',
      delaySearch: null,
      selectedCollections: [],
      searchedCountry: '',
      isSearched: false,
      collectionHeaders: [
        { text: '', value: 'name', sortable: false }
      ]
    }
  },
  computed: {
    currencyBySearchedCountry () {
      if (!this.searchedCountry) return ''
      const c = this.shippingCountries.find(
        (x) => x.code === this.searchedCountry
      )
      return c?.currency || this.searchedCountry
    },
    headers () {
      const headers = [
        { text: 'IMAGE', value: 'images', sortable: false, align: 'center' },
        { text: 'BRAND', value: 'brand', sortable: false, align: 'center' },
        { text: 'COLLECTION', value: 'collection', sortable: false, align: 'center' },
        { text: 'LAUNCH MONTH', value: 'launchMonth', sortable: false, width: '50px', align: 'center' },
        { text: 'DESIGNS/COLLECTION', value: 'countByCollection', sortable: false, width: '10px', align: 'center' },
        { text: 'PRODUCT NAME', value: 'productName', sortable: false, align: 'center', width: '200px' },
        { text: 'MODEL (5 DIGIT)', value: 'model5Digit', sortable: false, align: 'center' },
        { text: 'MODEL (6 DIGIT)', value: 'model', sortable: false, align: 'center' },
        { text: 'MER CODE', value: 'merCode', sortable: false, align: 'center' },
        { text: 'SKU CODE', value: 'code', sortable: false, align: 'center' },
        { text: 'SKU BARCODE', value: 'aliasId', sortable: false, align: 'center' },
        { text: 'RETAIL PRICE PER UNIT (THB)', value: 'thbPrice', sortable: false, width: '150px', align: 'center' },
        {
          text: `RETAIL PRICE PER UNIT ${this.currency ? `(${
            this.isSearched
              ? (this.shippingCountries.find((c) => c.code === this.searchedCountry)?.currency || this.searchedCountry)
              : (this.shippingCountries[0]?.currency || '')
          })` : ''}`,
          value: 'price',
          sortable: false,
          width: '150px',
          align: 'center'
        },
        { text: 'SIZE RANGE', value: 'sizeRange', sortable: false, width: '200px', align: 'center' },
        { text: 'GENDER', value: 'gender', sortable: false, align: 'center' },
        { text: 'COLOR', value: 'color.name', sortable: false, align: 'center' },
        { text: 'SIZE', value: 'size.name', sortable: false, width: '60px', align: 'center' },
        { text: 'GLOBAL CATEGORY', value: 'globalCategory', sortable: false, width: '350px', align: 'center' },
        { text: 'MAIN CATEGORY', value: 'mainCategory', sortable: false, width: '150px', align: 'center' },
        { text: 'SUB CATEGORY', value: 'subCategory', sortable: false, align: 'center' },
        { text: 'COL CODE', value: 'color.id', sortable: false, width: '100px', align: 'center' },
        { text: 'FABRIC COMPOSITION', value: 'fabricComposition', sortable: false, width: '300px', align: 'center' },
        { text: 'GARMENT TYPE', value: 'garmentType', sortable: false, width: '150px', align: 'center' },
        { text: 'COO', value: 'manufacCountry', sortable: false, align: 'center' },
        { text: 'FACTORY', value: 'factory', sortable: false, width: '100px', align: 'center' },
        { text: 'CERTIFICATE OF ORIGIN ( FORM D/FORM E )', value: 'certificateOfOrigin', sortable: false, width: '170px', align: 'center' },
        { text: 'HS CODE', value: 'hsCode', sortable: false, align: 'center' },
        { text: 'HS CODE FROM DISTRIBUTOR', value: 'hsCodeFromDistributor', sortable: false, width: '150px', align: 'center' },
        { text: 'QTY', value: 'qty', sortable: false, align: 'center' },
        { text: 'QR CODE', value: 'qrCode', sortable: false, align: 'center' }
      ]
      return headers
    },
    isMobile () {
      return this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm || this.$vuetify.breakpoint.md
    },
    currency () {
      return this.shippingCountries.find((c) => c.code === this.formData.country)?.currency || ''
    },
    filteredCollections () {
      const selectedIds = this.selectedCollections

      const allItems = [...this.collections, ...this.selectedCollectionObjects]
      const uniqueItems = Array.from(new Map(allItems.map((item) => [item.id, item])).values())

      const selected = uniqueItems.filter((item) => selectedIds.includes(item.id))
      const unselected = uniqueItems.filter((item) => !selectedIds.includes(item.id))

      return {
        selected,
        unselected,
        all: [...selected, ...unselected]
      }
    }
  },
  watch: {
    'options.page': {
      handler () {
        this.getItems()
      },
      deep: true
    },
    'options.sortBy': {
      handler () {
        this.getItems(true)
      },
      deep: true
    },
    'options.sortDesc': {
      handler () {
        this.getItems(true)
      },
      deep: true
    },
    collectionSearch () {
      clearTimeout(this.delaySearch)

      this.delaySearch = setTimeout(() => {
        this.optionsCollection.page = 1

        if (this.$refs.collectionList && this.$refs.collectionList.$el) {
            this.$refs.collectionList.$el.scrollTop = 0
        }

        this.getCollection(false, true)
      }, 300)
    },
    'selectedCollections': {
      deep: true,
      handler (val) {
        this.formData.collections = val
        this.isSearched = true
        this.searchedCountry = this.formData.country
        this.options.page = 1
        this.getItems()

        this.selectedCollectionObjects = this.selectedCollectionObjects.filter((obj) => val.includes(obj.id))

        val.forEach((id) => {
          const foundInList = this.collections.find((c) => c.id === id)
          const alreadySaved = this.selectedCollectionObjects.find((c) => c.id === id)

          if (foundInList && !alreadySaved) {
            this.selectedCollectionObjects.push(foundInList)
          }
        })
      }
    }
  },
  async mounted () {
    this.loading = true
    await this.getCountries()
    await this.getItems()
    await this.getCollection()
    this.loading = false
  },
  methods: {
    ...mapActions({
      setErrorPage: 'Components/setErrorPage',
      setSnackbar: 'Components/setSnackbar'
    }),
    toggleCollection (id) {
      const index = this.selectedCollections.indexOf(id)

      if (index === -1) {
        this.selectedCollections.push(id)
      } else {
        this.selectedCollections.splice(index, 1)
      }
    },
    async getCollection (append = false, reset = false) {
      if (this.loadingCollection) return
      try {
        if (reset) {
          this.optionsCollection.page = 1
          this.totalPageCollections = 0
        }

        this.loadingCollection = true
        const sortBy = 'createdAt'
        const sortOrder = 'desc'

        const { data } = await ProductCollectionService.getAll({
          ...this.optionsCollection,
          sortBy,
          sortOrder,
          search: this.collectionSearch
        })

        this.totalItems = data.total
        this.totalPageCollections = data.pages

        if (append) {
          this.collections.push(...data.results)
        } else {
          this.collections = data.results
        }
      } catch (error) {
        console.error('getCollection: ', error)
      } finally {
        this.loadingCollection = false
      }
    },
    onScroll (e) {
      const el = e.target
      const bottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 20

      if (bottom && !this.loadingCollection && this.optionsCollection.page < this.totalPageCollections) {
        this.optionsCollection.page++
        this.getCollection(true)
      }
    },
    getShippingCountry (code) {
      return ShippingCountries.find((country) => country.code === code)?.country || code
    },
    showPriceFormat (text) {
      const num = parseFloat(text)

      if (!Number.isNaN(num)) {
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(num)
      }

      return ''
    },
    async getCountries () {
      try {
        const { data } = await ExportCurrencyRatesService.getItems({})

        this.shippingCountries = data.map((r) => ({
          country: this.getShippingCountry(r.country),
          code: r.country,
          currency: r.currency
        }))

        if (this.shippingCountries.length > 0) {
          this.formData.country = this.shippingCountries[0].code
        }
        return true
      } catch (error) {
        console.error('getCountries', error)
        this.setSnackbar({
          value: true,
          message: `Error ${error.code} : ${error.message}`,
          type: 'error'
        })
        return false
      }
    },
    async generateQrCode (skuCode) {
      if (!skuCode) return ''
      try {
        const subBrandCode = skuCode.split('.')[0]?.toUpperCase()
        const baseUrl = this.storeWebUrl[subBrandCode]
        return await QRCode.toDataURL(`${baseUrl}/${skuCode}`, {
          margin: 1,
          width: 120
        })
      } catch (error) {
        console.error('generateQrCode', error)
        return ''
      }
    },
    getMerCode (name) {
      if (name.includes('-')) {
        return name.split('-').pop().trim()
      }
      return name.trim()
    },
    lazyImageUrl (src) {
      return src ? this.$convertImgix(`${src}?w=2&h=2&fit=clamp`) : this.noPicture
    },
    imageUrl (src) {
      return src ? this.$convertImgix(`${src}?w=128`) : this.noPicture
    },
    sortSize (skus) {
      return [...skus].sort(
        (a, b) => SizeValues(a.size.name) - SizeValues(b.size.name)
      )
    },
    searchProduct () {
      this.isSearched = true
      this.searchedCountry = this.formData.country
      this.loading = false
      this.options.page = 0

      this.product = []
      this.$router.push({
        name: 'ProductInformation',
        query: {
          search: this.formData.search,
          brand: this.formData.brand,
          collections: this.formData.collections
        }
      }).catch((error) => {
        console.error(error)
      })
      this.getItems()
    },
    async getItems () {
      try {
        this.loading = true
        const { data } = await productService.getProductsInformation({
          page: this.options.page,
          limit: this.options.limit,
          search: this.formData.search,
          brand: this.formData.brand,
          tags: this.formData.tags,
          tagOperation: this.formData.tagOperation,
          collections: this.formData.collections,
          country: this.formData.country,
          sortBy: this.options.sortBy,
          sortDesc: this.options.sortDesc
        })
        const tableData = data.results.flatMap((product) => {
          const sortedSkus = this.sortSize(product?.skus ?? [])
          const sortSizeRange = [
            ...new Set(sortedSkus.map((s) => s.size?.name))
          ].join(', ')
          return sortedSkus.map((sku) => ({
            ...sku,
            brand: product?.brand?.toUpperCase(),
            collection: product?.gwCollection?.name?.toUpperCase(),
            launchMonth: product?.gwCollection?.salesDate ? this.$dayjs(product?.gwCollection?.salesDate).format('MM/YYYY') : '',
            productName: product?.name?.toUpperCase(),
            model5Digit: product?.model?.substring(0, product?.model?.lastIndexOf('.')),
            model: product?.model,
            gender: product?.gender?.toUpperCase(),
            globalCategory: product?.globalCategory?.toUpperCase(),
            thbPrice: product?.price,
            sizeRange: sortSizeRange?.toUpperCase(),
            mainCategory: product?.productPrototype?.category?.toUpperCase(),
            subCategory: product?.productPrototype?.subCategory?.toUpperCase(),
            countByCollection: product?.countByCollection,
            garmentType: product?.garmentType?.toUpperCase(),
            fabrics: product?.fabrics,
            manufacCountry: product?.manufacCountry?.toUpperCase(),
            factory: product?.factory?.name?.toUpperCase() || '',
            certificateOfOrigin: product?.certificateOfOrigin?.toUpperCase(),
            hsCode: product?.hsCode
          }))
        })
        this.options.page = data.page
        this.totalProduct = data.total
        this.items = await Promise.all(tableData.map(async (row) => ({
          ...row,
          qrCode: await this.generateQrCode(row.code)
        })))
        this.pageCount = data.pages
        this.loading = false
      } catch (error) {
        console.error(error)
        this.setSnackbar({
          value: true,
          message: `Error ${error.code} : ${error.message}`,
          type: 'error'
        })
      } finally {
        this.isModalOpen = false
        this.loading = false
      }
    },
    async exportProduct () {
      try {
        this.loading = true
        const payload = {
          limit: 500,
          country: this.formData.country,
          sortBy: this.options.sortBy[0],
          sortDesc: this.options.sortDesc[0]
        }

        const totalRound = Math.ceil(this.totalProduct / payload.limit)

        if (this.isSearched) {
          payload.search = this.formData.search
          payload.brand = this.formData.brand
          payload.collections = this.formData.collections
          payload.tags = this.formData.tags
          payload.tagOperation = this.formData.tagOperation
        }

        for (let page = 1; page <= totalRound; page++) {
          payload.page = page

          // eslint-disable-next-line no-await-in-loop
          const data = await productService.exportProductInformation(payload)

          if (data) {
            const bom = '\uFEFF'
            const blob = new Blob([bom + data], { type: 'text/csv;charset=utf-8;' })
            const url = window.URL.createObjectURL(blob)

            const link = document.createElement('a')
            link.href = url
            const random = new Date().getTime()
            link.setAttribute('download', `Product-Information-${random}-${page.toString().padStart(3, '0')}.csv`)
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
          }
        }
      } catch (error) {
        console.error(error)
        this.setSnackbar({
          value: true,
          message: `Error ${error.code} : ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },

    async exportForUpdate () {
      try {
        this.loading = true
        const payload = {
          limit: 1000,
          page: 1,
          country: this.formData.country,
          sortBy: this.options.sortBy[0],
          sortDesc: this.options.sortDesc[0]
        }

        const totalRound = Math.ceil(this.totalProduct / payload.limit)

        if (this.isSearched) {
          payload.search = this.formData.search
          payload.brand = this.formData.brand
          payload.collections = this.formData.collections
          payload.tags = this.formData.tags
          payload.tagOperation = this.formData.tagOperation
        }

        for (let page = 1; page <= totalRound; page++) {
          payload.page = page

          // eslint-disable-next-line no-await-in-loop
          const data = await productService.exportProductForUpdate(payload)

          if (data) {
            const bom = '\uFEFF'
            const blob = new Blob([bom + data], { type: 'text/csv;charset=utf-8;' })
            const url = window.URL.createObjectURL(blob)

            const link = document.createElement('a')
            link.href = url
            const random = new Date().getTime()
            link.setAttribute('download', `Product-For-Update-${random}-${page.toString().padStart(3, '0')}.csv`)
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
          }
        }
      } catch (error) {
        console.error(error)
        this.setSnackbar({
          value: true,
          message: `Error ${error.code} : ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.toolbar {
  position: sticky;
  top: 70px;
  left: 0;
  width: 100%;
  z-index: 5;
  background-color: #fff;
  padding: 1.25rem 1.875rem;
  border-radius: 0.625rem;
}
.search-tag .v-input__control {
  max-width: unset !important;
  min-height: 28px !important;
  max-height: 28px !important;
}
.search-tag .v-input__slot {
  max-width: unset !important;
  min-height: 28px !important;
  max-height: 28px !important;
}
.collections-container {
  background-color: #fff;
  border-radius: 0.625rem;
  padding: 10px;
  height: calc(100vh - 285px);
}
.table-wrapper {
  position: relative;
  background-color: #fff;
  padding: 10px;
  border-radius: 0.625rem;
  height: calc(100vh - 285px);
}
.table-body {
  height: 100%;
  overflow-y: hidden;
}
.table-collection {
  height: 100%;
}
.list-collection {
  overflow-y: auto;
  height: calc(100vh - 380px);
}
</style>
